import React, { useState } from 'react';
import Modal from './Modal';
import { useAddin } from '../App';
import * as webhook from '../services/webhook';
import { MODELS, TONES } from '../utils/constants';

export function DraftReplyModal({ isOpen, onClose }) {
    const { linkedMatter, userEmail, senderInfo, itemId, conversationId, userSettings } = useAddin();
    const [submitting, setSubmitting] = useState(false);
    const [draftResult, setDraftResult] = useState(null);
    const [feedback, setFeedback] = useState(null);

    // Form State
    const activeModels = userSettings?.customModels || MODELS;
    const [tone, setTone] = useState(TONES[0]);
    const [model, setModel] = useState(userSettings?.defaultModel || 'claude-sonnet');
    const [instructions, setInstructions] = useState('');
    const [contextFlags, setContextFlags] = useState({
        threadHistory: true,
        recentActivity: false,
        priorComms: false,
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setDraftResult(null);

        try {
            const savedInstructions = userSettings?.instructions || {};
            const generalInstructions = savedInstructions.general?.trim() || '';
            const toneInstructions = savedInstructions.toneOverrides?.[tone]?.trim() || '';

            const activeModel = activeModels.find(m => m.id === model) || activeModels[0];

            const result = await webhook.draftReply({
                matterId: linkedMatter?.id || linkedMatter?.matter_id,
                matterNumber: linkedMatter?.display_number || linkedMatter?.number || linkedMatter?.matter_number,
                conversationId,
                itemId,
                userEmail,
                tone,
                model,
                modelProvider: activeModel.provider,
                modelApiId: activeModel.apiId,
                context_included: contextFlags.threadHistory,
                thread_limit: 5,
                matter_notes: [],
                recent_activity: contextFlags.recentActivity,
                prior_activity: contextFlags.priorComms,
                attachments: [],
                general_instructions: generalInstructions,
                tone_instructions: toneInstructions,
                additional_instructions: instructions
            });
            setDraftResult(result.draft || 'No draft was returned by the AI.');
            setFeedback({ type: 'success', message: 'Draft generated successfully' });
        } catch (error) {
            console.error('Failed to generate draft', error);
            setFeedback({ type: 'error', message: 'Failed to generate draft reply.' });
        } finally {
            setSubmitting(false);
        }
    };

    const handleCopy = () => {
        if (!draftResult) return;
        navigator.clipboard.writeText(draftResult)
            .then(() => setFeedback({ type: 'success', message: 'Draft copied to clipboard' }))
            .catch(() => setFeedback({ type: 'error', message: 'Failed to copy to clipboard' }));
    };

    return (
        <Modal isOpen={isOpen} title="Draft AI Reply" onClose={onClose}>
            {!draftResult ? (
                <form onSubmit={handleSubmit} className="entry-form popup-form">
                    <p className="form-helper" style={{ marginBottom: '16px' }}>
                        Generate an AI response based on the email thread context.
                    </p>

                    <div className="form-group">
                        <label>AI Model</label>
                        <select
                            className="form-input"
                            value={model}
                            onChange={(e) => setModel(e.target.value)}
                            style={{ width: '100%', marginTop: '5px' }}
                        >
                            {activeModels.map((m) => (
                                <option key={m.id} value={m.id}>
                                    {m.name} ({m.provider})
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="form-group" style={{ marginTop: '12px' }}>
                        <label htmlFor="toneSelect">Response Tone</label>
                        <select
                            id="toneSelect"
                            className="form-input"
                            value={tone}
                            onChange={(e) => setTone(e.target.value)}
                            required
                        >
                            {TONES.map(t => (
                                <option key={t} value={t}>{t}</option>
                            ))}
                        </select>
                    </div>

                    <div className="form-group" style={{ marginTop: '12px' }}>
                        <label>Include Context</label>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '6px' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', cursor: 'pointer' }}>
                                <input type="checkbox" checked={contextFlags.threadHistory} onChange={e => setContextFlags(p => ({ ...p, threadHistory: e.target.checked }))} />
                                Email Thread History
                            </label>
                            {linkedMatter && (
                                <>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', cursor: 'pointer' }}>
                                        <input type="checkbox" checked={contextFlags.recentActivity} onChange={e => setContextFlags(p => ({ ...p, recentActivity: e.target.checked }))} />
                                        Recent Matter Activity
                                    </label>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', cursor: 'pointer' }}>
                                        <input type="checkbox" checked={contextFlags.priorComms} onChange={e => setContextFlags(p => ({ ...p, priorComms: e.target.checked }))} />
                                        Prior Communications Log
                                    </label>
                                </>
                            )}
                        </div>
                    </div>

                    <div className="form-group" style={{ marginTop: '12px' }}>
                        <label htmlFor="instructionsInput">Custom Instructions (Optional)</label>
                        <textarea
                            id="instructionsInput"
                            className="form-input"
                            placeholder="e.g. Tell them the documents will be ready by Friday..."
                            value={instructions}
                            onChange={(e) => setInstructions(e.target.value)}
                            rows={3}
                        />
                    </div>

                    {/* Feedback */}
                    {feedback && (
                        <div className={`modal-feedback ${feedback.type}`} style={{ marginBottom: '16px' }}>
                            {feedback.type === 'success' ? '✓' : '⚠'} {feedback.message}
                        </div>
                    )}

                    <div className="form-actions" style={{ marginTop: '20px' }}>
                        <button type="button" className="btn-secondary" onClick={onClose} disabled={submitting}>
                            Cancel
                        </button>
                        <button type="submit" className="btn-primary" disabled={submitting}>
                            {submitting ? 'Generating...' : 'Generate Draft'}
                        </button>
                    </div>
                </form>
            ) : (
                <div className="draft-result-container" style={{ padding: '0 5px' }}>
                    <div className="form-group">
                        <label>Generated Draft</label>
                        <textarea
                            className="form-input"
                            value={draftResult}
                            onChange={(e) => setDraftResult(e.target.value)}
                            rows={12}
                            style={{ fontSize: '12px', lineHeight: '1.4', resize: 'vertical' }}
                        />
                    </div>

                    {/* Feedback */}
                    {feedback && (
                        <div className={`modal-feedback ${feedback.type}`} style={{ marginBottom: '16px' }}>
                            {feedback.type === 'success' ? '✓' : '⚠'} {feedback.message}
                        </div>
                    )}

                    <div className="form-actions" style={{ marginTop: '20px' }}>
                        <button type="button" className="btn-secondary" onClick={() => setDraftResult(null)}>
                            Rewrite
                        </button>
                        <div style={{ display: 'flex', gap: '8px' }}>
                            <button type="button" className="btn-primary" onClick={handleCopy}>
                                Copy to Clipboard
                            </button>
                            <button type="button" className="btn-secondary" onClick={onClose}>
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </Modal>
    );
}
